import { alpha, createTheme, Theme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

// "Fonts should be big and nice" — requirement #14.
// Refined palette + softer shadows + smoother transitions for a more polished feel.
export function createAppTheme(mode: ThemeMode): Theme {
  const isDark = mode === 'dark';
  const primaryMain = isDark ? '#5b9eff' : '#0067c0';
  const secondaryMain = '#7e5cd9';

  return createTheme({
    palette: {
      mode,
      primary:   { main: primaryMain },
      secondary: { main: secondaryMain },
      success:   { main: isDark ? '#5ec48a' : '#0e8a3e' },
      warning:   { main: isDark ? '#f5b740' : '#b97700' },
      error:     { main: isDark ? '#ff7a7a' : '#c93636' },
      info:      { main: isDark ? '#5b9eff' : '#0067c0' },
      background: isDark
        ? { default: '#0d1117', paper: '#161b22' }
        : { default: '#f7f8fb', paper: '#ffffff' },
      divider: isDark ? alpha('#ffffff', 0.09) : alpha('#000000', 0.08),
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
      fontSize: 14,
      h1: { fontSize: '2.75rem', fontWeight: 700, letterSpacing: '-0.025em' },
      h2: { fontSize: '2rem',    fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontSize: '1.625rem', fontWeight: 700, letterSpacing: '-0.015em' },
      h4: { fontSize: '1.3rem',  fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontSize: '1.1rem',  fontWeight: 600 },
      h6: { fontSize: '0.95rem', fontWeight: 600 },
      body1: { fontSize: '0.9rem', lineHeight: 1.55 },
      body2: { fontSize: '0.825rem', lineHeight: 1.55 },
      button: { fontSize: '0.85rem', fontWeight: 600, textTransform: 'none', letterSpacing: '0.005em' },
      caption: { fontSize: '0.72rem', letterSpacing: '0.02em' },
    },
    shape: { borderRadius: 12 },
    shadows: [
      'none',
      isDark ? '0 1px 2px rgba(0,0,0,0.4)'  : '0 1px 2px rgba(15,23,42,0.06)',
      isDark ? '0 2px 6px rgba(0,0,0,0.45)' : '0 2px 8px rgba(15,23,42,0.08)',
      isDark ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 14px rgba(15,23,42,0.09)',
      isDark ? '0 6px 18px rgba(0,0,0,0.55)' : '0 6px 20px rgba(15,23,42,0.10)',
      ...Array(20).fill(isDark ? '0 8px 24px rgba(0,0,0,0.6)' : '0 10px 30px rgba(15,23,42,0.12)'),
    ] as Theme['shadows'],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: isDark
              ? 'radial-gradient(circle at 10% 0%, #1a2333 0%, #0d1117 60%)'
              : 'radial-gradient(circle at 10% 0%, #e7eef9 0%, #f7f8fb 60%)',
            backgroundAttachment: 'fixed',
          },
          // Smooth color transitions when toggling theme
          '*, *::before, *::after': {
            transition: 'background-color 200ms ease, border-color 200ms ease, color 200ms ease',
          },
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-track': { background: 'transparent' },
          '::-webkit-scrollbar-thumb': {
            background: isDark ? alpha('#ffffff', 0.12) : alpha('#000000', 0.16),
            borderRadius: 8,
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: isDark ? alpha('#ffffff', 0.2) : alpha('#000000', 0.28),
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'saturate(180%) blur(10px)',
            backgroundColor: isDark ? alpha('#161b22', 0.78) : alpha('#ffffff', 0.86),
            color: isDark ? '#e6edf3' : '#1f2937',
            borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000000', 0.07)}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#10151c' : '#ffffff',
            borderRight: `1px solid ${isDark ? alpha('#ffffff', 0.06) : alpha('#000000', 0.06)}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: isDark ? alpha(primaryMain, 0.16) : alpha(primaryMain, 0.10),
              color: primaryMain,
              '& .MuiListItemIcon-root': { color: primaryMain },
              '&:hover': { backgroundColor: isDark ? alpha(primaryMain, 0.22) : alpha(primaryMain, 0.14) },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { padding: '8px 18px', borderRadius: 10 },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.06) : alpha('#000000', 0.05)}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.06) : alpha('#000000', 0.05)}`,
            transition: 'transform 180ms ease, box-shadow 180ms ease',
            '&:hover': { transform: 'translateY(-2px)' },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? alpha('#ffffff', 0.03) : alpha('#000000', 0.025),
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            '& fieldset': {
              borderColor: isDark ? alpha('#ffffff', 0.12) : alpha('#000000', 0.12),
            },
            '&:hover fieldset': {
              borderColor: isDark ? alpha('#ffffff', 0.24) : alpha('#000000', 0.24),
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'medium' },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 8 },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: '0.72rem',
              color: isDark ? alpha('#ffffff', 0.7) : alpha('#000000', 0.7),
              borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#000000', 0.1)}`,
              background: isDark ? alpha('#ffffff', 0.02) : alpha('#000000', 0.02),
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06)}`,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
    },
  });
}

export const theme = createAppTheme('light');
